import { Search, X } from 'lucide-react';
import Avatar from '../Avatar';
import { KIND_DISPLAY } from '../../lib/map/festivalMapGeoJson';
import { CHIP_WORDS } from '../../lib/map/kinds';
import { iconMarkup } from '../../lib/map/mapIcons';
import { Highlight } from './FindWhoPicker';

/**
 * Find's top: the Maps page's search pill, its status button on the right, and its chips underneath (Stage 6, Sam
 * 2026-10-26: "just borrow what we have built for the maps"). No Back - Find is a tab - and no group card: the event is
 * the one you are at, and the radar shows everyone you are with there.
 *
 * Search answers places and people in one list: a place turns its chip on and picks that one, a person is tracked. The
 * chips are show filters, several at once; each puts its nearest few on the radar (lib/map/amenityFinder).
 */
export default function FindTopBar({
  query,
  onQuery,
  places = [],
  people = [],
  onPickPlace,
  onPickPerson,
  chipKinds = [],
  activeKinds,
  onToggleChip,
  status = null,
  searchable = true,
}) {
  const open = query.trim() && (places.length > 0 || people.length > 0);
  return (
    <div className="find-top">
      <div className="maps-page__topbar">
        {searchable && (
          <div className="maps-page__search">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder={chipKinds.length ? 'Search people and places' : 'Search people'}
              aria-label={chipKinds.length ? 'Search people and places' : 'Search people'}
              onChange={(e) => onQuery(e.target.value)}
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => onQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
        )}
        {status}
      </div>
      {open && (
        <ul className="maps-page__results find-top__results">
          {places.length > 0 && people.length > 0 && (
            <li className="maps-page__results-label">Places</li>
          )}
          {places.map((p) => (
            <li key={p.key}>
              <button type="button" onClick={() => onPickPlace(p)}>
                <span
                  className="maps-page__result-icon"
                  style={{ color: KIND_DISPLAY[p.kind]?.color }}
                  dangerouslySetInnerHTML={{ __html: iconMarkup(p.kind, 16) }}
                />
                <span>
                  <Highlight text={p.name} query={query} />
                </span>
              </button>
            </li>
          ))}
          {people.length > 0 && places.length > 0 && (
            <li className="maps-page__results-label">People</li>
          )}
          {people.map((p) => (
            <li key={`p-${p.peerId}`}>
              <button
                type="button"
                className="maps-page__result-person"
                onClick={() => onPickPerson(p.peerId)}
              >
                <Avatar displayName={p.displayName} profilePhotoUrl={p.photoUrl} size="xxs" />
                <span>
                  <Highlight text={p.displayName} query={query} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {chipKinds.length > 0 && (
        <div className="fmap-chips find-top__chips" role="group" aria-label="Show on the radar">
          {chipKinds.map((kind) => {
            const on = activeKinds.has(kind);
            return (
              <div key={kind} className="fmap-chip-wrap">
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={KIND_DISPLAY[kind]?.label}
                  className={`fmap-chip ${on ? 'fmap-chip--on' : ''}`}
                  style={{ '--chip-color': KIND_DISPLAY[kind]?.color }}
                  onClick={() => onToggleChip(kind)}
                  dangerouslySetInnerHTML={{ __html: iconMarkup(kind, 18) }}
                />
                <span className="fmap-chip-word">
                  {CHIP_WORDS[kind] ?? KIND_DISPLAY[kind]?.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
